import { ArpFields } from "constants/forecasts.constants";
import { EPSILON } from "utils/numbers";

import { ArpSegment } from "models/UserArpsModel";

/**
 * True when the product's terminal segment has no rate range (qi === qf).
 *
 * Switching a type well to SAGD and then to another template leaves a flat
 * terminal segment behind: the segments are not remapped to the new template's
 * shape. The EUR solve then has nowhere to put the cumulative -- it pushes the
 * preceding segment's qf above its qi, which the forecast cannot integrate, and
 * EUR comes back non-finite.
 *
 * This is a guard, not a cure. The defect is in the template conversion and
 * reproduces on main with no chart involved: switch to SAGD, switch to another
 * template, then edit EUR in the input table. Anchor drags are simply another
 * caller of the same EUR solve. Remove this guard once the conversion remaps
 * segments correctly.
 */
export function hasDegenerateTerminal(productArps: ArpSegment[]): boolean {
  const terminal = productArps[productArps.length - 1];
  if (!terminal) return false;
  // A non-finite rate is the corrupted case this guard exists to catch --
  // treat it as degenerate rather than falling through and letting the EUR
  // solve run against it. Uses the codebase's own EPSILON (1e-10, matching
  // isFlatSegment's identical qi/qf comparison) rather than Number.EPSILON
  // (2.2e-16), which is six orders of magnitude tighter and would call a
  // terminal degenerate to isFlatSegment but not to this guard.
  if (!Number.isFinite(terminal.qi) || !Number.isFinite(terminal.qf)) return true;
  return Math.abs(terminal.qi - terminal.qf) < EPSILON;
}

export interface AnchorDragPlan {
  /** Field to apply through updateArps. */
  header: string;
  value: number;
  /**
   * EUR to re-solve Di for after applying the field, or null to apply the field
   * alone and let EUR float.
   */
  preserveEur: Nullable<number>;
}

/**
 * Decide how an anchor drag should be applied.
 *
 * The invariant: dragging any anchor other than the EUR anchor must not move
 * EUR. Every non-EUR drag therefore carries the pre-drag EUR and re-solves Di
 * for it. The EUR anchor is the one that is allowed to change EUR, so it never
 * re-solves against a stale target.
 */
export function planAnchorDragApplication(params: {
  header: string;
  value: number;
  /** EUR read from the EUR anchor before the drag, if it was available. */
  targetEur: Nullable<number>;
  productArps: ArpSegment[];
}): AnchorDragPlan {
  const { header, value, targetEur, productArps } = params;

  // The EUR drag is the one that sets EUR; there is nothing to preserve.
  if (header === ArpFields.EUR) {
    return { header, value, preserveEur: null };
  }

  const canPreserve =
    typeof targetEur === "number" &&
    Number.isFinite(targetEur) &&
    !hasDegenerateTerminal(productArps);

  return { header, value, preserveEur: canPreserve ? targetEur : null };
}
